"use client";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { getPricingList } from "@/redux/Slice/PriceSlice";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_REDIRECT } from "@/app/Api/Route_API";
import Image from "next/image";

// Icon mapping for different plan types
const planIcons = {
  starter: "/assets/images/pricing-individual-icon.png",
  growth: "/assets/images/pricing-pro-icon.png",
  advanced: "/assets/images/pricing-enterprise-icon.png",
  pro: "/assets/images/pricing-pro-icon.png",
  "starter-inr": "/assets/images/pricing-individual-icon.png",
  "pro-inr": "/assets/images/pricing-pro-icon.png",
  "business-inr": "/assets/images/pricing-enterprise-icon.png",
  "free-inr": "/assets/images/pricing-individual-icon.png",
  free: "/assets/images/pricing-individual-icon.png",
};

export default function PriceComponents() {
  const [planType, setPlanType] = useState("monthly");
  const [audience, setAudience] = useState("business");
  const [expandedPlans, setExpandedPlans] = useState({});
  const priceDetails = useSelector((state) => state?.price?.price);
  const dispatch = useDispatch();
  const router = useRouter();

  useEffect(() => {
    dispatch(getPricingList());
  }, [dispatch]);

  // Toggle expanded state for a specific plan
  const togglePlanExpansion = (planKey) => {
    setExpandedPlans((prev) => ({
      ...prev,
      [planKey]: !prev[planKey],
    }));
  };

  // Handle plan selection and redirect
  const handlePlanSelection = (plan) => {
    if (!plan || !plan.id) {
      console.error("Plan details are required");
      return;
    }

    // Build the checkout URL with all plan parameters
    const checkoutParams = new URLSearchParams({
      planId: plan.id.toString(),
      name: plan.name,
      price: plan.price,
      description: plan.description || "",
      billing_period: plan.billing_period,
      currency: plan.currency || "INR",
      slug: plan.slug || "",
    });

    // Add payment method IDs if available (even for free plans, backend needs to know)
    if (plan.razorpay_plan_id) {
      checkoutParams.append("razorpay_plan_id", plan.razorpay_plan_id);
    }
    if (plan.paypal_plan_id) {
      checkoutParams.append("paypal_plan_id", plan.paypal_plan_id);
    }

    // Create the full checkout URL (works for both free and paid plans)
    const checkoutUrl = `/users/payments/checkout?${checkoutParams.toString()}`;

    // Encode the checkout URL and pass it to login page
    const loginParams = new URLSearchParams({
      redirect: encodeURIComponent(checkoutUrl),
    });

    // Redirect to login page with encoded checkout URL
    router.push(`${DEFAULT_REDIRECT}?${loginParams.toString()}`);
  };

  // Filter plans based on billing period
  const getPlansForPeriod = (period) => {
    if (!priceDetails || !Array.isArray(priceDetails)) return [];
    return priceDetails.filter((plan) => plan.billing_period === period);
  };

  // Get available features for a plan
  const getAvailableFeatures = (plan) => {
    if (!plan || !plan.all_features) return [];
    return plan.all_features.filter((feature) => feature.is_available);
  };

  // Format feature display
  const formatFeatureText = (plan, feature) => {
    const baseText = feature.name;

    switch (feature.slug) {
      case "priority-support":
        return feature.value || baseText;
      default:
        return baseText;
    }
  };

  const getPlanLimits = (plan) => {
    const limits = [];

    if (plan.words_per_month) {
      limits.push(`${plan.words_per_month.toLocaleString()} words per month`);
    }

    if (plan.outputs_per_month) {
      limits.push(`${plan.outputs_per_month} outputs per month`);
    }

    if (plan.user_seats) {
      limits.push(`Up to ${plan.user_seats} team members`);
    }

    if (plan.projects_limit) {
      limits.push(`${plan.projects_limit} projects limit`);
    }

    return limits;
  };

  const currentPlans = getPlansForPeriod(
    planType === "monthly" ? "monthly" : "yearly",
  );

  return (
    <div className="">
      <section
        style={{
          backgroundColor: "#F5F5F7",
          backgroundImage:
            "radial-gradient(58.91% 76.39% at 80% -10%, rgba(127, 161, 255, 0.18) 0%, rgba(127, 174, 255, 0.00) 60%), radial-gradient(58.91% 62.5% at -5% 30%, rgba(69, 85, 237, 0.14) 0%, rgba(69, 88, 237, 0.00) 55%), url(/assets/hero/grid.svg)",
          backgroundPosition: "0 0, 0 0, 0 -90px",
        }}
        className="relative w-full overflow-hidden px-5 sm:px-6 lg:px-20 pt-14 lg:pt-16 pb-28 lg:pb-40"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-40 lg:-top-48 z-0 w-[88%] max-w-[600px] h-[350px] lg:h-[350px] bg-white rounded-[50%] blur-[120px]"
        />
        <div className="relative z-10 max-w-4xl mx-auto flex flex-col items-center text-center">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex -space-x-3">
              {[
                "https://randomuser.me/api/portraits/men/1.jpg",
                "https://randomuser.me/api/portraits/women/2.jpg",
                "https://randomuser.me/api/portraits/men/3.jpg",
                "https://randomuser.me/api/portraits/women/4.jpg",
                "https://randomuser.me/api/portraits/men/5.jpg",
              ].map((src, i) => (
                <Image
                  key={i}
                  src={src}
                  width={32}
                  height={32}
                  alt={`user-${i + 1}`}
                  className="rounded-full ring-2 ring-white object-cover w-8 h-8"
                />
              ))}
            </div>
            <span className="text-sm font-semibold text-[#0E0B1A]">
              2,400+ Users Joined Our Platform
            </span>
          </div>

          <h1 className="text-[28px] sm:text-[38px] md:text-[40px] lg:text-[46px] 3xl:text-[64px] 4xl:text-[80px] font-medium tracking-[-0.5px] leading-[1.1] text-[#0E0B1A]">
            Pricing built for smarter ad creation
          </h1>

          <p className="mt-5 text-[#6D6D6D] text-[15px] sm:text-base lg:text-lg max-w-[560px] font-normal tracking-[-0.3px]">
            Choose a plan that fits your workflow solo or team. Simple, flexible
            pricing with no hidden fees.
          </p>
          <div className="mt-8 flex items-center justify-center">
            <div className="flex items-center gap-3 ">
              <span
                className={`text-sm font-medium transition-colors ${
                  planType === "monthly" ? "text-[#0E0B1A]" : "text-[#6D6D6D]"
                }`}
              >
                Monthly
              </span>

              <button
                type="button"
                role="switch"
                aria-checked={planType === "yearly"}
                onClick={() =>
                  setPlanType(planType === "monthly" ? "yearly" : "monthly")
                }
                className="relative h-7 w-[50px] rounded-full bg-[#151618] transition-colors"
              >
                <span
                  className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform duration-200 ${
                    planType === "yearly"
                      ? "-translate-x-0.5"
                      : "-translate-x-[22px]"
                  }`}
                />
              </button>
            </div>
            <div className="flex items-center min-w-[110px] ml-3">
              <span
                className={`text-sm font-medium transition-colors ${
                  planType === "yearly" ? "text-[#0E0B1A]" : "text-[#6D6D6D]"
                }`}
              >
                Annually
              </span>

              <span
                className={`ml-1 bg-[#E5F8EA] text-[#16A34A] px-2 py-0.5 rounded-full text-xs font-semibold transition-opacity ${
                  planType === "yearly" ? "opacity-100" : "opacity-0"
                }`}
              >
                Save 20%
              </span>
            </div>
          </div>

          {/* 

          <div className="inline-flex p-1 mt-6 rounded-xl border border-black/10 bg-white/60 backdrop-blur-sm">
            <button
              type="button"
              onClick={() => setAudience("business")}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                audience === "business"
                  ? "bg-white border border-black/10 shadow-sm text-[#0E0B1A]"
                  : "text-[#6D6D6D] hover:text-[#0E0B1A]"
              }`}
            >
              Businesses & Team
            </button>
            <button
              type="button"
              onClick={() => setAudience("enterprise")}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                audience === "enterprise"
                  ? "bg-white border border-black/10 shadow-sm text-[#0E0B1A]"
                  : "text-[#6D6D6D] hover:text-[#0E0B1A]"
              }`}
            >
              Enterprise
            </button>
          </div> */}
        </div>
      </section>

      <div className="pricing">
        <div className="relative">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-40 lg:-top-48 z-0 w-[88%] max-w-[1200px] h-[560px] lg:h-[680px] bg-white rounded-[50%] blur-[120px]"
          />
          <div className="flex flex-wrap justify-center items-start gap-5 px-5 sm:px-6 lg:px-20 -mt-12 lg:-mt-16 pb-16 relative z-10">
            {currentPlans.length > 0 ? (
              currentPlans.map((plan) => {
                const availableFeatures = getAvailableFeatures(plan);
                const planLimits = getPlanLimits(plan);
                const allFeatures = [
                  ...planLimits,
                  ...availableFeatures.map((f) => formatFeatureText(plan, f)),
                ];
                const planKey = `${plan.slug}-${plan.billing_period}`;
                const isExpanded = expandedPlans[planKey];
                const displayFeatures = isExpanded
                  ? allFeatures
                  : allFeatures.slice(0, 8);

                const isPopular =
                  plan.slug === "growth" ||
                  plan.slug === "pro-inr" ||
                  plan.slug === "pro";

                const isFree = parseFloat(plan.price) === 0;
                const currencySymbol = plan.currency === "USD" ? "$" : "₹";
                const billingLabel =
                  plan.billing_period === "monthly"
                    ? "/user/month"
                    : "/user (billed annually)";

                const CardBody = (
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-[20px] font-semibold text-[#0D0F12] leading-none">
                      {plan.name}
                    </p>

                    {isFree ? (
                      <p className="mt-5 flex items-baseline">
                        <span className="text-[30px] font-semibold leading-none text-[#0E0B1A]">
                          FREE
                        </span>
                      </p>
                    ) : (
                      <p className="mt-5 flex items-baseline flex-wrap">
                        <span className="text-[22px] font-semibold text-[#0D0F12]">
                          {currencySymbol}
                        </span>
                        <span className="text-[30px] font-semibold leading-none text-[#0D0F12]">
                          {plan.price}
                        </span>
                        <span className="ml-1.5 text-[13px] text-[#8A909E]">
                          {billingLabel}
                        </span>
                      </p>
                    )}

                    <p className="mt-4 text-[14px] text-[#8A909E] leading-[1.5]">
                      {plan.description ||
                        "Perfect for individuals exploring Imagive.ai"}
                    </p>

                    <button
                      onClick={() => handlePlanSelection(plan)}
                      className={`${
                        isPopular ? "btn-primary" : "btn-secondary"
                      } mt-6 w-full font-medium`}
                    >
                      {isFree ? "Get Started Free" : `Get ${plan.name}`}
                    </button>

                    {isPopular && (
                      <p className="mt-7 text-[14px] font-semibold text-[#0E0B1A]">
                        Everything in Starter, plus:
                      </p>
                    )}

                    <ul
                      className={`${isPopular ? "mt-4" : "mt-7"} space-y-3 flex-1`}
                    >
                      {displayFeatures.map((feature, i) => (
                        <li
                          key={i}
                          className="flex  items-center gap-2.5 text-[14px] text-[#0E0B1A]"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <rect
                              x="0.245166"
                              y="0.245166"
                              width="17.4826"
                              height="17.4826"
                              rx="8.74128"
                              stroke="black"
                              strokeOpacity="0.1"
                              strokeWidth="0.490332"
                            />
                            <g clipPath="url(#clip0_2058_6145)">
                              <path
                                d="M5.61719 8.74023L7.61719 10.7402L11.6172 6.74023"
                                stroke="black"
                                strokeWidth="1.471"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_2058_6145">
                                <rect
                                  width="8.98645"
                                  height="8.98645"
                                  fill="white"
                                  transform="translate(4.49316 4.49316)"
                                />
                              </clipPath>
                            </defs>
                          </svg>

                          <span>{feature}</span>
                        </li>
                      ))}

                      {allFeatures.length > 8 && (
                        <li>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePlanExpansion(planKey);
                            }}
                            className="text-[14px] font-medium text-[#2D2D97] hover:underline"
                          >
                            {isExpanded
                              ? "Show less"
                              : `Show ${allFeatures.length - 8} more features`}
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>
                );

                return isPopular ? (
                  <div
                    key={planKey}
                    className="relative bg-gradient-to-b p-0.5 from-[#3232A4] to-[#13133E] rounded-[22px] overflow-hidden flex flex-col w-full sm:w-[calc(50%-20px)] lg:w-[calc(25%-20px)] 2xl:w-[calc(22%-20px)] lg:-mt-8 shadow-[0_10px_40px_rgba(45,45,151,0.18)] transition-all duration-300"
                  >
                    <p className="text-center text-white text-[10px] py-1  font-medium tracking-wide">
                      Most Popular
                    </p>
                    <div className="bg-white rounded-[20px] flex flex-col flex-1">
                      {CardBody}
                    </div>
                  </div>
                ) : (
                  <div
                    key={planKey}
                    className="relative bg-white rounded-[22px] border border-gray-200 overflow-hidden flex flex-col w-full sm:w-[calc(50%-20px)] lg:w-[calc(25%-20px)] 2xl:w-[calc(22%-20px)] transition-all duration-300"
                  >
                    {CardBody}
                  </div>
                );
              })
            ) : (
              <div className="flex justify-center items-center w-full h-[400px]">
                <div className="text-center">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-500 mb-4"></div>
                  <p className="text-[#6F6C90] text-[16px]">
                    Loading pricing plans...
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
